import { useEffect, useRef, useState } from "react";
import type { FairReport } from "@fira/contracts";

interface FairOption {
  id: string;
  name: string;
  status: string;
  days: { businessDate: string }[];
}
const money = (cents: number) =>
  new Intl.NumberFormat("ca-ES", { style: "currency", currency: "EUR" }).format(
    cents / 100,
  );
const date = (value: string) =>
  value.slice(0, 10).split("-").reverse().join("/");
const time = (value: string) =>
  new Intl.DateTimeFormat("ca-ES", {
    dateStyle: "short",
    timeStyle: "short",
    timeZone: "Europe/Madrid",
  }).format(new Date(value));
const payment = (method: string) =>
  method === "CASH" ? "Efectiu" : method === "CARD" ? "Targeta" : "Altres";

export function ReportsPanel() {
  const [events, setEvents] = useState<FairOption[]>([]);
  const [eventId, setEventId] = useState("");
  const [mode, setMode] = useState("all");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [type, setType] = useState("tickets");
  const [report, setReport] = useState<FairReport | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const controller = useRef<AbortController | null>(null);
  useEffect(() => {
    const abort = new AbortController();
    void fetch("/api/v1/operations/overview", {
      credentials: "include",
      signal: abort.signal,
    })
      .then(async (response) => {
        if (!response.ok) throw new Error("No s'han pogut carregar les fires.");
        const data = (await response.json()) as { events: FairOption[] };
        if (abort.signal.aborted) return;
        setEvents(data.events);
        setEventId(
          data.events.find((event) => event.status === "OPEN")?.id ??
            data.events[0]?.id ??
            "",
        );
      })
      .catch((reason) => {
        if (!abort.signal.aborted) setError(reason.message);
      });
    return () => {
      abort.abort();
      controller.current?.abort();
    };
  }, []);
  const selected = events.find((event) => event.id === eventId);
  const days = [...(selected?.days ?? [])]
    .map((day) => day.businessDate)
    .sort();
  const first = from || days[0] || "";
  const last = to || days[days.length - 1] || "";
  const invalid =
    mode !== "all" && (!first || !last || (mode === "range" && first > last));
  function invalidate() {
    setReport(null);
    setError("");
  }
  async function generate() {
    setBusy(true);
    setError("");
    setReport(null);
    const abort = new AbortController();
    controller.current = abort;
    const query = new URLSearchParams({ eventId });
    if (mode !== "all") {
      query.set("from", first);
      query.set("to", mode === "day" ? first : last);
    }
    try {
      const response = await fetch(`/api/v1/reports/fair?${query}`, {
        credentials: "include",
        cache: "no-store",
        signal: abort.signal,
      });
      const data = await response.json();
      if (!response.ok)
        throw new Error(data.message ?? "No s'ha pogut generar l'informe.");
      if (!abort.signal.aborted) setReport(data as FairReport);
    } catch (reason) {
      if (!abort.signal.aborted)
        setError(
          reason instanceof Error ? reason.message : "Error de comunicació.",
        );
    } finally {
      if (!abort.signal.aborted) setBusy(false);
    }
  }
  const options = days.map((day) => (
    <option key={day} value={day}>
      {date(day)}
    </option>
  ));
  return (
    <div className="reports-panel">
      <section className="panel report-controls">
        <div className="report-filter-grid">
          <label>
            Fira
            <select
              aria-label="Fira"
              value={eventId}
              disabled={busy || !events.length}
              onChange={(event) => {
                setEventId(event.target.value);
                setFrom("");
                setTo("");
                invalidate();
              }}
            >
              {!events.length && (
                <option value="">No hi ha fires disponibles</option>
              )}
              {events.map((event) => (
                <option key={event.id} value={event.id}>
                  {event.name} ·{" "}
                  {event.status === "CLOSED"
                    ? "Tancada"
                    : event.status === "OPEN"
                      ? "Oberta"
                      : "Esborrany"}
                </option>
              ))}
            </select>
          </label>
          <label>
            Jornades
            <select
              aria-label="Jornades"
              value={mode}
              disabled={busy}
              onChange={(event) => {
                setMode(event.target.value);
                invalidate();
              }}
            >
              <option value="all">Tota la fira</option>
              <option value="day">Una jornada</option>
              <option value="range">Interval de jornades</option>
            </select>
          </label>
          {mode !== "all" && (
            <label>
              {mode === "day" ? "Jornada" : "Des de"}
              <select
                value={first}
                disabled={busy || !days.length}
                onChange={(event) => {
                  setFrom(event.target.value);
                  invalidate();
                }}
              >
                {options}
              </select>
            </label>
          )}
          {mode === "range" && (
            <label>
              Fins a
              <select
                value={last}
                disabled={busy || !days.length}
                onChange={(event) => {
                  setTo(event.target.value);
                  invalidate();
                }}
              >
                {options}
              </select>
            </label>
          )}
          <label>
            Tipus d'informe
            <select
              aria-label="Tipus d'informe"
              value={type}
              onChange={(event) => setType(event.target.value)}
            >
              <option value="tickets">Detall de tiquets</option>
              <option value="products">
                Productes agrupats · regularització d'estoc
              </option>
            </select>
          </label>
        </div>
        <div className="report-buttons">
          <button
            className="primary"
            type="button"
            disabled={busy || !eventId || invalid}
            onClick={() => void generate()}
          >
            {busy ? "Generant…" : "Generar / actualitzar l'informe"}
          </button>
          <button
            className="secondary"
            type="button"
            disabled={!report || busy || !report.tickets.length}
            onClick={() => window.print()}
          >
            Imprimir / desar com a PDF
          </button>
        </div>
        <p>
          Impressió a la impressora d'aquest ordinador. Sense dependre del
          Bridge ni enviar dades a Àgora.
        </p>
        {invalid && (
          <p role="alert">Selecciona un interval de jornades vàlid.</p>
        )}
        {error && (
          <p className="alert error" role="alert">
            {error}
          </p>
        )}
      </section>
      {!report && !busy && (
        <p className="report-controls">
          Selecciona la fira i genera l'informe. No s'actualitza automàticament
          mentre el revises.
        </p>
      )}
      {report && (
        <section
          className="report-document"
          aria-label="Vista prèvia de l'informe"
        >
          <header className="report-title">
            <div>
              <p className="eyebrow">EIRA SEREN · FIRA TPV</p>
              <h2>
                {type === "tickets"
                  ? "Informe detallat de tiquets"
                  : "Relació de productes agrupats"}
              </h2>
              <h3>{report.event.name}</h3>
            </div>
            <div>
              <p>
                {report.event.status === "CLOSED"
                  ? "Fira tancada"
                  : "Informe provisional · fira no tancada"}
              </p>
              <small>Generat: {time(report.generatedAt)}</small>
            </div>
          </header>
          <p>
            <strong>Jornades:</strong>{" "}
            {report.scope.from
              ? `${date(report.scope.from)} — ${date(report.scope.to ?? report.scope.from)}`
              : "Tota la fira"}
            {report.days.length > 0 &&
              ` · ${report.days.map((day) => date(day.businessDate)).join(", ")}`}
          </p>
          <p className="report-notice">
            Origen: vendes sincronitzades al backend. Les vendes encara pendents
            als terminals no s'inclouen. Els tiquets anul·lats es mostren, però
            no compten als imports ni a les quantitats netes.
          </p>
          <div className="report-totals">
            <article>
              <span>Import net</span>
              <strong>{money(report.summary.totalCents)}</strong>
            </article>
            <article>
              <span>Tiquets vàlids</span>
              <strong>{report.summary.completed}</strong>
              <small>
                {report.summary.cancelled} anul·lats · {report.tickets.length}{" "}
                tiquets registrats
              </small>
            </article>
            <article>
              <span>Unitats netes</span>
              <strong>{report.summary.netUnits}</strong>
            </article>
            <article>
              <span>Pagaments nets</span>
              <strong>{money(report.summary.cashCents)} efectiu</strong>
              <small>
                {money(report.summary.cardCents)} targeta ·{" "}
                {money(report.summary.otherCents)} altres
              </small>
            </article>
          </div>
          <table className="report-table report-day-table">
            <thead>
              <tr>
                <th>Jornada</th>
                <th>Vàlids</th>
                <th>Anul·lats</th>
                <th>Import net</th>
              </tr>
            </thead>
            <tbody>
              {report.days.map((day) => (
                <tr key={day.businessDate}>
                  <td>{date(day.businessDate)}</td>
                  <td>{day.completed}</td>
                  <td>{day.cancelled}</td>
                  <td>{money(day.totalCents)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {!report.tickets.length && (
            <p>No hi ha tiquets per a aquestes jornades.</p>
          )}
          {type === "products" ? (
            <>
              <p>
                Venudes: quantitat original, incloses les vendes després
                anul·lades. Netes = venudes − anul·lades.
              </p>
              <table className="report-table report-product-table">
                <thead>
                  <tr>
                    <th>Codi</th>
                    <th>Producte</th>
                    <th>Venudes</th>
                    <th>Anul·lades</th>
                    <th>Netes</th>
                  </tr>
                </thead>
                <tbody>
                  {report.products.map((product) => (
                    <tr key={product.productId}>
                      <td>
                        <span>ID Àgora: {product.agoraId}</span>
                        <small>
                          {product.barcode
                            ? `Codi: ${product.barcode}`
                            : "Sense codi de barres"}
                        </small>
                      </td>
                      <td>{product.name}</td>
                      <td>{product.sold}</td>
                      <td>{product.cancelled}</td>
                      <td>
                        <strong>{product.net}</strong>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <th colSpan={4}>Total d'unitats netes</th>
                    <th>{report.summary.netUnits}</th>
                  </tr>
                </tfoot>
              </table>
            </>
          ) : (
            report.tickets.map((ticket) => (
              <article
                className={`report-ticket ${ticket.status === "CANCELLED" ? "report-cancelled" : ""}`}
                key={ticket.id}
              >
                <header>
                  <div>
                    <h3>Tiquet {ticket.reference}</h3>
                    <p>
                      Jornada {date(ticket.businessDate)} ·{" "}
                      {time(ticket.occurredAt)} · Terminal: {ticket.deviceName}
                    </p>
                  </div>
                  <strong>{money(ticket.totalCents)}</strong>
                </header>
                <small className="report-ticket-id">ID: {ticket.id}</small>
                <p>
                  {ticket.payments.map((entry, index) => (
                    <span key={index}>
                      {index > 0 ? " · " : ""}
                      {payment(entry.method)}: {money(entry.amountCents)}
                    </span>
                  ))}
                </p>
                {ticket.status === "CANCELLED" && (
                  <p>
                    <strong>ANUL·LAT · exclòs dels totals nets.</strong>{" "}
                    {ticket.cancellationReason}
                    {ticket.cancelledAt && ` · ${time(ticket.cancelledAt)}`}
                  </p>
                )}
                <table className="report-table">
                  <thead>
                    <tr>
                      <th>Codi</th>
                      <th>Producte</th>
                      <th>Quantitat</th>
                      <th>Preu unitari</th>
                      <th>Import</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ticket.lines.map((line, index) => (
                      <tr key={index}>
                        <td>
                          {line.agoraId}
                          <small>{line.barcode ?? "Sense codi"}</small>
                        </td>
                        <td>{line.name}</td>
                        <td>{line.quantity}</td>
                        <td>{money(line.unitPriceCents)}</td>
                        <td>{money(line.subtotalCents)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </article>
            ))
          )}
          <footer className="report-footer">
            Document auxiliar de control intern i regularització d'estoc. No és
            un tiquet fiscal ni acredita la importació a Àgora. ·{" "}
            {report.event.name} · {time(report.generatedAt)}
          </footer>
        </section>
      )}
    </div>
  );
}
